import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import Container from '@components/ContentContainer';
import Header from '@components/Contact/Header';
import Bio from '@components/Contact/Bio';
import Experience from '@components/Contact/Experience';
import Education from '@components/Contact/Education';
import Social from '@components/Contact/Social';
import Websites from '@components/Contact/Websites';
import Interests from '@components/Contact/Interests';
import { NotesList } from '@components/Contact/Notes';
import { SharedContactHighlightsList } from '@components/Contact/Highlights';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '@hooks/use-auth-context';
import { useAutoFocus } from '@hooks/use-auto-focus';
import { useScrollingClass } from '@hooks/use-scrolling-class';
import { selectSharedContact } from '@reducers/sharedData/contacts';
import { fetchSharedContact } from '@actions/shared/contacts';
import { fetchSharedContactNotes } from '@actions/shared/notes';
import { selectSharedContactNotes } from '@reducers/sharedData/notes';
import { fetchSharedContactHighlights } from '@actions/shared/highlights';
import { selectSharedContactHighlights } from '@reducers/sharedData/highlights';
import styles from './SharedContact.module.scss';
import { fetchSharedContactMoments } from '@actions/shared/moments';
import { selectSharedContactInteractionSummary } from '@reducers/sharedData/moments';
import { fetchSharedContactSources } from '@actions/shared/sources';
import { selectSharedContactSources } from '@reducers/sharedData/sources';
import { ContactViewSidebarDisplay } from '@components/Contact/Sidebar';
import SharedViewWrapper from '@components/Sharing/SharedViewWrapper';
import SharedResourceUtilityBar from '@components/Sharing/SharedResourceUtilityBar';

export default function SharedContact() {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { uuid: contactUUID } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayLogIn, setDisplayLogIn] = useState(false);
  const contact = useSelector(selectSharedContact(contactUUID));
  const notes = useSelector(selectSharedContactNotes(contactUUID));
  const highlights = useSelector(selectSharedContactHighlights(contactUUID));
  const summary = useSelector(selectSharedContactInteractionSummary(contactUUID));
  const sources = useSelector(selectSharedContactSources(contactUUID));

  useEffect(() => {
    dispatch(fetchSharedContact(contactUUID, user));
    dispatch(fetchSharedContactNotes(contactUUID, user));
    dispatch(fetchSharedContactHighlights(contactUUID, user));
    dispatch(fetchSharedContactMoments(contactUUID, user));
    dispatch(fetchSharedContactSources(contactUUID, user));
  }, [contactUUID]);

  useScrollingClass(containerRef, 'contact-scrolled');
  useAutoFocus(containerRef);

  return (
    <SharedViewWrapper
      sharedResourceType="contact"
      displayLogIn={displayLogIn}
      error={contact.error}
    >
      <Container key={contactUUID}>
        <Helmet title={contact.displayName} />
        <Container.Primary
          ref={containerRef}
          className={styles.container}
          header={
            <SharedResourceUtilityBar
              sharedResource={contact}
              sharedResourceType="contact"
              logIn={() => setDisplayLogIn(true)}
            >
              <span className={styles.contactName}>{contact.displayName}</span>
            </SharedResourceUtilityBar>
          }
        >
          <div className={styles.content}>
            <Header contact={contact} />
            <NotesList notes={notes} readOnly />
            <Bio contact={contact} />
            <Social contact={contact} />
            <SharedContactHighlightsList highlights={highlights} />
            <Experience contact={contact} />
            <Education contact={contact} />
            <Websites contact={contact} />
            <Interests contact={contact} clickable={!!user} />
          </div>
        </Container.Primary>
        <ContactViewSidebarDisplay
          className={!user && styles.unauthenticatedUser}
          contact={contact}
          sources={sources}
          summary={summary}
        />
      </Container>
    </SharedViewWrapper>
  );
}
